import React, { useEffect, useMemo, useState } from "react";

const Backend = () => {
  const [isOpen, setIsOpen] = useState(false);

  const skillsCatalog = useMemo(
    () => [
      {
        name: "Node.js",
        level: "Advanced",
        icon: "bxl-nodejs",
        desc: "Server-side runtime for building fast APIs and backend services.",
      },
      {
        name: "Express.js",
        level: "Advanced",
        icon: "bxs-file-js",
        desc: "Lightweight Node framework for REST APIs, middleware, and routing.",
      },
      {
        name: "MongoDB",
        level: "Advanced",
        icon: "bxl-mongodb",
        desc: "NoSQL database for document storage, indexing, and aggregation.",
      },
      {
        name: "Mongoose (ODM)",
        level: "Advanced",
        icon: "bx-badge-check",
        desc: "Schema + data modeling layer for MongoDB (validation, hooks, relations).",
      },
      {
        name: "Python",
        level: "Advanced",
        icon: "bxl-python",
        desc: "Backend scripting language for APIs, automation, and data workflows.",
      },
      {
        name: "FastAPI / Flask",
        level: "Advanced",
        icon: "bx-server",
        desc: "Python frameworks for building APIs (FastAPI for speed + type safety).",
      },
      {
        name: "JWT & RBAC Auth",
        level: "Advanced",
        icon: "bx-shield-quarter",
        desc: "Authentication & authorization (tokens, roles, permissions, guards).",
      },
      {
        name: "Validation (Zod/Pydantic)",
        level: "Advanced",
        icon: "bxs-zap",
        desc: "Strong request/response validation to prevent bad data and bugs.",
      },
      {
        name: "SQL (PostgreSQL/MySQL)",
        level: "Intermediate",
        icon: "bx-data",
        desc: "Relational databases for structured data, reporting, and transactions.",
      },
      {
        name: "Caching (Redis patterns)",
        level: "Intermediate",
        icon: "bx-timer",
        desc: "Improve response times using caching, TTL, and invalidation strategies.",
      },
      {
        name: "Docker",
        level: "Intermediate",
        icon: "bxl-docker",
        desc: "Containerize services for consistent deployment and environments.",
      },
      {
        name: "CI/CD",
        level: "Advanced",
        icon: "bx-git-branch",
        desc: "Automated build/test/deploy pipelines for reliable releases.",
      },
      {
        name: "Webhooks & Event Systems",
        level: "Advanced",
        icon: "bx-sitemap",
        desc: "Build event-driven integrations between services and third-party tools.",
      },
      {
        name: "BaaS (Appwrite/Firebase)",
        level: "Advanced",
        icon: "bxl-firebase",
        desc: "Backend services for auth, data, storage, and serverless workflows.",
      },
      {
        name: "Security (CORS/Rate Limits/Hashing)",
        level: "Advanced",
        icon: "bx-lock-alt",
        desc: "Secure APIs using best practices: hashing, headers, limits, sanitization.",
      },
      {
        name: "Logging & Monitoring",
        level: "Advanced",
        icon: "bx-bug",
        desc: "Trace issues with structured logs, error tracking, and performance insights.",
      },
      {
        name: "Payments (Paystack/Flutterwave/Stripe)",
        level: "Advanced",
        icon: "bx-credit-card",
        desc: "Payment integration with verification, webhooks, and transaction tracking.",
      },
    ],
    []
  );

  // Show only a few on the page (clean UI)
  const topSkills = useMemo(() => skillsCatalog.slice(0, 5), [skillsCatalog]);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  // ESC to close + lock body scroll
  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  return (
    <div className="skillsContent">
      <h3 className="skillsTitle">Backend & Python Web Development</h3>

      <div className="skillsBox">
        <div className="skillsGroup">
          {topSkills.map((skill) => (
            <div className="skillsData" key={skill.name}>
              <i className={`bx ${skill.icon}`}></i>
              <div>
                <h3 className="skillsName">{skill.name}</h3>
                <span className="skillsLevel">{skill.level}</span>
              </div>
            </div>
          ))}

          {/* CTA */}
          <button className="skillsMoreBtn" type="button" onClick={openModal}>
            <span className="skillsMoreBtn__pulse" aria-hidden="true"></span>
            I can do more — <strong>View full catalog</strong>
            <i className="bx bx-right-arrow-alt"></i>
          </button>
        </div>
      </div>

      {/* MODAL */}
      {isOpen && (
        <div className="skillsModalOverlay" role="dialog" aria-modal="true">
          <div className="skillsModalBackdrop" onClick={closeModal} />

          <div className="skillsModal">
            <div className="skillsModalHeader">
              <div>
                <h3 className="skillsModalTitle">Backend Skill Catalog</h3>
                <p className="skillsModalSubtitle">
                  A quick overview of tools/frameworks I use in production backend & Python web work.
                </p>
              </div>

              <button
                className="skillsModalClose"
                type="button"
                onClick={closeModal}
                aria-label="Close"
              >
                <i className="bx bx-x"></i>
              </button>
            </div>

            <div className="skillsCatalog">
              {skillsCatalog.map((skill, idx) => (
                <div
                  className="skillsCard"
                  key={skill.name}
                  style={{ animationDelay: `${idx * 25}ms` }}
                >
                  <div className="skillsCardTop">
                    <i className={`bx ${skill.icon}`}></i>
                    <div className="skillsCardMeta">
                      <h4 className="skillsCardTitle">{skill.name}</h4>
                      <span className="skillsCardLevel">{skill.level}</span>
                    </div>
                  </div>
                  <p className="skillsCardDesc">{skill.desc}</p>
                </div>
              ))}
            </div>

            <div className="skillsModalFooter">
              <span className="skillsHint">
                Tip: press <kbd>ESC</kbd> to close
              </span>
              <button className="skillsModalPrimary" type="button" onClick={closeModal}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Backend;
